<script setup>
/**
 * @fileoverview ConsoleToolbar - Console controls and status display
 *
 * Toolbar component for VM console with navigation, fullscreen toggle,
 * clipboard controls, and special key combinations.
 *
 * @example
 * <ConsoleToolbar
 *   vm-name="Web Server"
 *   connection-state="connected"
 *   @back="handleBack"
 *   @fullscreen="toggleFullscreen"
 *   @sendClipboard="handleSendClipboard"
 *   @sendCtrlAltDel="handleCtrlAltDel"
 * />
 */

import { ArrowLeft, Maximize, Minimize, Clipboard, Keyboard, Send, X } from 'lucide-vue-next'
import { ref } from 'vue'

/**
 * @typedef {Object} Props
 * @property {string} vmName - Display name of the VM
 * @property {'idle'|'connecting'|'connected'|'disconnected'|'reconnecting'|'error'} connectionState - Current connection state
 * @property {number} [reconnectAttempts] - Current reconnection attempt count
 */
const props = defineProps({
  vmName: {
    type: String,
    required: true
  },
  connectionState: {
    type: String,
    required: true,
    validator: value =>
      ['idle', 'connecting', 'connected', 'disconnected', 'reconnecting', 'error'].includes(value)
  },
  reconnectAttempts: {
    type: Number,
    default: 0
  }
})

/**
 * @typedef {Object} Emits
 * @emits back - User clicked back button
 * @emits fullscreen - User toggled fullscreen mode
 * @emits sendClipboard - User wants to send clipboard text to remote
 * @emits sendCtrlAltDel - User wants to send Ctrl+Alt+Del
 * @emits dialogOpen - Clipboard dialog opened (disable keyboard capture)
 * @emits dialogClose - Clipboard dialog closed (re-enable keyboard capture)
 */
const emit = defineEmits([
  'back',
  'fullscreen',
  'sendClipboard',
  'sendCtrlAltDel',
  'dialogOpen',
  'dialogClose'
])

// Track fullscreen state
const isFullscreen = ref(false)

// Clipboard dialog state
const showClipboardDialog = ref(false)
const clipboardText = ref('')

/**
 * Handle back button click
 */
function handleBack() {
  emit('back')
}

/**
 * Toggle fullscreen mode
 */
function toggleFullscreen() {
  isFullscreen.value = !isFullscreen.value
  emit('fullscreen', isFullscreen.value)
}

/**
 * Open clipboard send dialog
 */
function openClipboardDialog() {
  showClipboardDialog.value = true
  clipboardText.value = ''
  emit('dialogOpen')
}

/**
 * Close clipboard dialog
 */
function closeClipboardDialog() {
  showClipboardDialog.value = false
  clipboardText.value = ''
  emit('dialogClose')
}

/**
 * Send clipboard text to remote VM
 */
function sendClipboard() {
  if (clipboardText.value.trim()) {
    emit('sendClipboard', clipboardText.value)
    closeClipboardDialog()
  }
}

/**
 * Send Ctrl+Alt+Del to remote VM
 */
function sendCtrlAltDel() {
  emit('sendCtrlAltDel')
}

/**
 * Get human-readable connection state text
 * @returns {string} Display text for current state
 */
function getStateText() {
  if (props.connectionState === 'reconnecting') {
    return `Reconnecting (${props.reconnectAttempts}/5)`
  }

  const states = {
    idle: 'Initializing',
    connecting: 'Connecting',
    connected: 'Connected',
    disconnected: 'Disconnected',
    error: 'Connection Error'
  }
  return states[props.connectionState] || 'Unknown'
}

/**
 * Get Tailwind color classes for connection state badge
 * @returns {string} Tailwind classes for badge styling
 */
function getStateBadgeClasses() {
  const classes = {
    idle: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
    connecting:
      'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/10 dark:text-amber-400 dark:border-amber-800',
    reconnecting:
      'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/10 dark:text-amber-400 dark:border-amber-800 animate-pulse',
    connected:
      'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/10 dark:text-emerald-400 dark:border-emerald-800',
    disconnected:
      'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
    error:
      'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/10 dark:text-red-400 dark:border-red-800'
  }
  return classes[props.connectionState] || classes.idle
}

/**
 * Check if console controls should be disabled
 * @returns {boolean} True if controls should be disabled
 */
function isControlsDisabled() {
  return props.connectionState !== 'connected'
}
</script>

<template>
  <div
    class="console-toolbar bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700"
  >
    <div class="flex items-center justify-between px-4 py-3">
      <!-- Left Section: Back Button & VM Name -->
      <div class="flex items-center gap-2 sm:gap-4">
        <button
          @click="handleBack"
          class="inline-flex items-center gap-2 px-3 py-2 min-h-[44px] min-w-[44px] sm:min-w-0 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
          aria-label="Go back"
        >
          <ArrowLeft class="w-5 h-5 sm:w-4 sm:h-4" />
          <span class="hidden sm:inline">Back</span>
        </button>

        <div class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <h2 class="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
            {{ vmName }}
          </h2>

          <!-- Connection Status Badge -->
          <span
            :class="[
              'inline-flex items-center px-2.5 sm:px-3 py-1 rounded-full text-xs font-medium border',
              getStateBadgeClasses()
            ]"
            role="status"
            aria-live="polite"
            :aria-label="`Console connection status: ${getStateText()}`"
          >
            <span
              v-if="connectionState === 'connected'"
              class="w-2 h-2 rounded-full bg-current mr-2 animate-pulse"
              aria-hidden="true"
            ></span>
            <span
              v-else-if="connectionState === 'connecting'"
              class="w-2 h-2 rounded-full bg-current mr-2"
              aria-hidden="true"
            ></span>
            {{ getStateText() }}
          </span>
        </div>
      </div>

      <!-- Right Section: Console Controls -->
      <div class="flex items-center gap-1.5 sm:gap-2">
        <!-- Send Clipboard -->
        <button
          @click="openClipboardDialog"
          :disabled="isControlsDisabled()"
          class="inline-flex items-center justify-center gap-2 px-2.5 sm:px-3 py-2 min-h-[44px] min-w-[44px] rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          :aria-label="
            isControlsDisabled()
              ? 'Send clipboard to VM (disabled - not connected)'
              : 'Send clipboard to VM'
          "
          title="Send clipboard text to VM"
        >
          <Clipboard class="w-5 h-5 sm:w-4 sm:h-4" aria-hidden="true" />
          <span class="hidden lg:inline">Send Clipboard</span>
        </button>

        <!-- Send Ctrl+Alt+Del -->
        <button
          @click="sendCtrlAltDel"
          :disabled="isControlsDisabled()"
          class="inline-flex items-center justify-center gap-2 px-2.5 sm:px-3 py-2 min-h-[44px] min-w-[44px] rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          :aria-label="
            isControlsDisabled()
              ? 'Send Ctrl+Alt+Del (disabled - not connected)'
              : 'Send Ctrl+Alt+Del to VM'
          "
          title="Send Ctrl+Alt+Del (useful for Windows VMs)"
        >
          <Keyboard class="w-5 h-5 sm:w-4 sm:h-4" aria-hidden="true" />
          <span class="hidden lg:inline">Ctrl+Alt+Del</span>
        </button>

        <!-- Fullscreen Toggle -->
        <button
          @click="toggleFullscreen"
          class="inline-flex items-center justify-center gap-2 px-2.5 sm:px-3 py-2 min-h-[44px] min-w-[44px] rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
          :aria-label="isFullscreen ? 'Exit fullscreen mode' : 'Enter fullscreen mode'"
          :aria-pressed="isFullscreen"
        >
          <Maximize v-if="!isFullscreen" class="w-5 h-5 sm:w-4 sm:h-4" aria-hidden="true" />
          <Minimize v-else class="w-5 h-5 sm:w-4 sm:h-4" aria-hidden="true" />
          <span class="hidden md:inline">
            {{ isFullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}
          </span>
        </button>
      </div>
    </div>

    <!-- Clipboard Send Dialog -->
    <div
      v-if="showClipboardDialog"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="closeClipboardDialog"
    >
      <div
        class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4"
        role="dialog"
        aria-labelledby="clipboard-dialog-title"
        aria-modal="true"
      >
        <!-- Dialog Header -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700"
        >
          <h3
            id="clipboard-dialog-title"
            class="text-lg font-semibold text-gray-900 dark:text-white"
          >
            Send Clipboard to VM
          </h3>
          <button
            @click="closeClipboardDialog"
            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
            aria-label="Close dialog"
          >
            <X class="w-5 h-5" />
          </button>
        </div>

        <!-- Dialog Body -->
        <div class="px-6 py-4">
          <label
            for="clipboard-text"
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Enter text to send
          </label>
          <textarea
            id="clipboard-text"
            v-model="clipboardText"
            rows="6"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            placeholder="Paste or type text here..."
            autofocus
            @keydown.stop
            @keyup.stop
            @keypress.stop
          ></textarea>
          <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
            This text will be sent to the VM's clipboard and can be pasted inside the VM.
          </p>
        </div>

        <!-- Dialog Footer -->
        <div
          class="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700"
        >
          <button
            @click="closeClipboardDialog"
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            @click="sendClipboard"
            :disabled="!clipboardText.trim()"
            class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Send clipboard text to virtual machine"
          >
            <Send class="w-4 h-4" aria-hidden="true" />
            Send to VM
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.console-toolbar {
  /* Ensure toolbar stays on top */
  position: relative;
  z-index: 10;
}
</style>
